import { Icon } from '@advanza/ui'
import clsx from 'clsx'
import A from 'components/ui/A'
import React from 'react'
import {
    getChurnReasonDisplayText,
    getProviderStatusDisplayText,
    PROVIDER_STATUS_COLORS,
    PROVIDER_STATUS_ICONS,
} from './constants'

// Renders provider status with icon and styling (consistent across reports)
export const renderProviderStatus = (
    status: string | number,
    key: string = 'provider_status',
): React.ReactElement => {
    // Convert numeric status code to display text if needed
    const displayText = typeof status === 'number' ? getProviderStatusDisplayText(status) : status

    return (
        <div key={key} className="flex items-center gap-2">
            <Icon
                name={PROVIDER_STATUS_ICONS[displayText] || 'beach_access'}
                fontSize={14}
                classNameIcon={PROVIDER_STATUS_COLORS[displayText] || 'text-fallback-pink'}
            />
            <span className="text-sm font-medium">{displayText}</span>
        </div>
    )
}

// Renders churn reason with proper display text
export const renderChurnReason = (
    reason: string | number | null,
    key: string = 'churn_reason',
): React.ReactElement => {
    if (!reason) {
        return (
            <span key={key} className="text-neutral-400">
                -
            </span>
        )
    }

    const displayText = getChurnReasonDisplayText(reason)

    return (
        <span key={key} className="text-md">
            {displayText}
        </span>
    )
}

// Renders subscription status with appropriate badge styling
export const renderSubscriptionStatus = (
    status: string | null | undefined,
    key: string = 'subscription_status',
): React.ReactElement => {
    // Providers that saw a win-back offer but never had a presence subscription
    // (e.g. churners with no subscription row) have no status; surface that
    // explicitly with a neutral grey pill rather than an empty cell.
    if (status === null || status === undefined) {
        return (
            <span
                key={key}
                className="px-2 py-0.5 rounded text-sm font-bold bg-neutral-100 text-neutral-600">
                null
            </span>
        )
    }

    const styleClass = (() => {
        if (status === 'active') {
            return 'bg-secondary-100 text-secondary-800'
        }
        if (status === 'pending') {
            return 'bg-tertiary-100 text-tertiary-800'
        }
        return 'bg-red-100 text-red-800'
    })()

    return (
        <span key={key} className={clsx('px-2 py-0.5 rounded text-sm font-bold', styleClass)}>
            {status}
        </span>
    )
}

// Renders prospect value with goldenrod styling
export const renderProspectValue = (
    value: number | null | undefined,
    key: string = 'prospect_value',
): React.ReactElement => {
    if (value === null || value === undefined || value === 0) {
        return <span key={key}>-</span>
    }

    return (
        <span key={key} className="text-goldenrod font-medium">
            {'€'.repeat(value)}
        </span>
    )
}

// Renders provider ID as a clickable link
export const renderProviderId = (
    providerId: string | number,
    key: string = 'provider_id',
    baseUrl: string = '/office/providers',
): React.ReactElement => {
    return (
        <A key={key} className="classic" to={`${baseUrl}/${providerId}`} external={false}>
            #{providerId}
        </A>
    )
}

// Renders a date with fallback for null/undefined values
export const renderDate = (date: string | null | undefined, key: string): React.ReactElement => {
    if (!date) {
        return (
            <span key={key} className="text-neutral-400">
                -
            </span>
        )
    }

    return <span key={key}>{new Date(date).toLocaleDateString('en-CA')}</span>
}

// Renders a boolean value with check/cancel icons
export const renderBoolean = (
    value: boolean,
    key: string,
    trueText: string = 'Yes',
    falseText: string = 'No',
): React.ReactElement => {
    return (
        <div key={key} className="flex items-center gap-1">
            {value ? (
                <>
                    <Icon name="check_circle" classNameIcon="text-success" fontSize={16} />
                    <span className="text-success font-medium">{trueText}</span>
                </>
            ) : (
                <>
                    <Icon name="cancel" classNameIcon="text-error" fontSize={16} />
                    <span className="text-error font-medium">{falseText}</span>
                </>
            )}
        </div>
    )
}

// Renders text with fallback for null/undefined values
export const renderText = (
    value: string | null | undefined,
    key: string,
    fallback: string = '-',
): React.ReactElement => {
    return <span key={key}>{value || fallback}</span>
}

// Renders currency values with euro symbol and formatting
export const renderCurrency = (
    value: number | string | null | undefined,
    key: string,
): React.ReactElement => {
    if (value === null || value === undefined) {
        return (
            <span key={key} className="text-neutral-400">
                -
            </span>
        )
    }

    return <span key={key}>€{parseFloat(value.toString()).toFixed(2)}</span>
}
